import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { adService } from '../../services/adService';
import ImageUpload from '../common/ImageUpload';
import { societyService, extractSocietyResults } from '../../services/societyService';
import SocietySearchableSelect from '../common/SocietySearchableSelect';
import { getAdUrl } from '../../utils/urlHelpers';

function createClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `ad-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

const INITIAL_FORM_DATA = {
  title: '',
  description: '',
  category: 'other',
  price: '',
  location: '',
  contact_phone: '',
  society: '',
  is_for_all_societies: false,
  nearest_landmark: '',
};

const AdCreateForm = ({ initialAd = null, isEditMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [societies, setSocieties] = useState([]);
  const [loadingSocieties, setLoadingSocieties] = useState(true);
  const submitInFlightRef = useRef(false);
  const clientRequestIdRef = useRef(null);

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [userSociety, setUserSociety] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagesToRemove, setImagesToRemove] = useState([]);
  const [displayImageIndex, setDisplayImageIndex] = useState(null);

  // Prefill form when editing
  useEffect(() => {
    if (!initialAd || !isEditMode) return;
    const societyId = initialAd.society?.id ?? initialAd.society;
    setFormData({
      title: initialAd.title ?? '',
      description: initialAd.description ?? '',
      category: initialAd.category ?? 'other',
      price: initialAd.price ?? '',
      location: initialAd.location ?? '',
      contact_phone: initialAd.contact_phone ?? '',
      society: societyId ?? '',
      is_for_all_societies: !!(initialAd.is_for_all_societies ?? initialAd.is_public),
      nearest_landmark: initialAd.location ?? ''
    });
    if (initialAd?.images?.length) {
      const coverId =
        initialAd.display_image?.id ??
        initialAd.images.find((img) => img.is_featured)?.id;
      if (coverId != null) {
        const idx = initialAd.images.findIndex((img) => img.id === coverId);
        if (idx >= 0) setDisplayImageIndex(idx);
      }
    }
  }, [initialAd, isEditMode]);

  // Existing ad images for edit mode: { id, url } (API returns id and original_image URL)
  const existingImages = (isEditMode && initialAd?.images?.length)
    ? initialAd.images
        .map((img) => {
          const url = typeof img.original_image === 'string' ? img.original_image : img.original_image?.url;
          return url && img.id != null ? { id: img.id, url } : null;
        })
        .filter(Boolean)
    : [];
  const keptExistingImages = existingImages.filter((img) => !imagesToRemove.includes(img.id));

  const remapDisplayIndexAfterRemove = (removedGlobalIndex) => {
    setDisplayImageIndex((prev) => {
      if (prev === null) return null;
      const remainingCount =
        keptExistingImages.length - 1 + selectedImages.length;
      if (prev === removedGlobalIndex) {
        return remainingCount > 0 ? 0 : null;
      }
      if (prev > removedGlobalIndex) return prev - 1;
      return prev;
    });
  };

  // Load user's society (for display and submit); admins still get user society for "Visible to all" vs "My society"
  useEffect(() => {
    const loadSocieties = async () => {
      try {
        setLoadingSocieties(true);
        const userIsAdmin = user && (
          user.is_staff ||
          user.is_superuser ||
          ['admin', 'moderator'].includes(user.role)
        );
        setIsAdmin(userIsAdmin);

        if (user && user.society) {
          setUserSociety(user.society);
          setSocieties([user.society]);
          if (!isEditMode) {
            setFormData((prev) => ({ ...prev, society: user.society.id }));
          }
        } else if (userIsAdmin) {
          const response = await societyService.getSocieties({ page_size: 500 });
          const list = extractSocietyResults(response);
          setSocieties(list);
          if (!isEditMode && list.length) {
            setFormData((prev) => ({ ...prev, society: list[0].id }));
          }
        } else if (user) {
          const response = await societyService.getSocieties({ page_size: 500 });
          const list = extractSocietyResults(response);
          setSocieties(list);
          if (!isEditMode) {
            const societyIdFromUrl = searchParams.get('society');
            if (societyIdFromUrl) {
              const sid = parseInt(societyIdFromUrl, 10);
              const selected = list.find((s) => s.id === sid);
              if (selected) {
                setFormData((prev) => ({ ...prev, society: selected.id }));
              }
            } else if (list.length) {
              setFormData((prev) => ({ ...prev, society: list[0].id }));
            }
          }
        }
      } catch (error) {
        console.error('Error loading societies:', error);
        showError('Failed to load societies. Please try again.');
      } finally {
        setLoadingSocieties(false);
      }
    };

    if (user) {
      loadSocieties();
    }
  }, [showError, user, isEditMode, searchParams]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const getSocietySlugFromPath = () => {
    if (!location.pathname.includes('/societies/')) return null;
    const pathParts = location.pathname.split('/');
    const societySlugIndex = pathParts.indexOf('societies') + 1;
    return pathParts[societySlugIndex] || null;
  };

  const resetFormAfterSuccess = () => {
    setFormData({
      ...INITIAL_FORM_DATA,
      society: userSociety?.id ?? formData.society ?? societies[0]?.id ?? '',
    });
    setSelectedImages([]);
    setImagesToRemove([]);
    setDisplayImageIndex(null);
    clientRequestIdRef.current = null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitInFlightRef.current) return;

    submitInFlightRef.current = true;
    setLoading(true);

    try {
      const societyId = formData.is_for_all_societies
        ? (userSociety ? userSociety.id : formData.society)
        : formData.society;
      const locationText = userSociety
        ? (formData.nearest_landmark
          ? `${userSociety.name}, ${formData.nearest_landmark}`
          : userSociety.name)
        : formData.nearest_landmark || formData.location;
      const totalImages = keptExistingImages.length + selectedImages.length;
      let coverIndex = displayImageIndex;
      if (totalImages > 0 && coverIndex === null) {
        coverIndex = 0;
      }
      if (coverIndex !== null && coverIndex >= totalImages) {
        coverIndex = 0;
      }

      const adData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        contact_phone: formData.contact_phone,
        society: societyId,
        is_for_all_societies: formData.is_for_all_societies,
        location: locationText,
        images: selectedImages,
      };
      if (formData.price !== '' && formData.price != null) {
        adData.price = formData.price;
      }
      if (selectedImages.length > 0 && coverIndex !== null) {
        adData.display_image_index = coverIndex;
      }

      if (isEditMode && initialAd) {
        for (const imageId of imagesToRemove) {
          await adService.deleteAdImage(initialAd.slug, imageId);
        }
        await adService.updateAd(initialAd.slug, adData);
        showSuccess('Ad updated successfully!');
        const societySlug = getSocietySlugFromPath();
        if (societySlug) {
          navigate(`/societies/${societySlug}/ads`);
        } else {
          navigate('/ads');
        }
      } else {
        if (!clientRequestIdRef.current) {
          clientRequestIdRef.current = createClientRequestId();
        }
        const createdAd = await adService.createAd(adData, {
          clientRequestId: clientRequestIdRef.current,
        });
        resetFormAfterSuccess();
        showSuccess('Ad created successfully! It will be reviewed by admin before being published.');
        const societySlug = getSocietySlugFromPath();
        const adKey = createdAd?.slug || createdAd?.id;
        if (adKey) {
          navigate(getAdUrl(createdAd, societySlug), { replace: true });
        } else if (societySlug) {
          navigate(`/societies/${societySlug}/ads`, { replace: true });
        } else {
          navigate('/ads', { replace: true });
        }
      }
    } catch (error) {
      console.error(isEditMode ? 'Error updating ad:' : 'Error creating ad:', error);
      const message =
        error?.message ||
        (isEditMode ? 'Failed to update ad. Please try again.' : 'Failed to create ad. Please try again.');
      showError(typeof message === 'string' ? message : 'Failed to save ad. Please try again.');
    } finally {
      setLoading(false);
      submitInFlightRef.current = false;
    }
  };

  const categoryOptions = [
    { value: 'car', label: 'Car' },
    { value: 'clothing', label: 'Clothing' },
    { value: 'electronics', label: 'Electronics' },
    { value: 'property', label: 'Property' },
    { value: 'services', label: 'Services' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">{isEditMode ? 'Edit Ad' : 'Create New Ad'}</h1>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Show in all societies vs only this society */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_for_all_societies"
              name="is_for_all_societies"
              checked={formData.is_for_all_societies}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_for_all_societies" className="ml-2 block text-sm text-gray-700">
              Show in all societies
            </label>
          </div>

          {/* Society: All Societies when is_for_all_societies, else user society or admin dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Society
            </label>
            {formData.is_for_all_societies ? (
              <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                All Societies
              </div>
            ) : isAdmin && societies.length > 0 ? (
              <SocietySearchableSelect
                societies={societies}
                value={formData.society}
                onChange={handleChange}
                loading={loadingSocieties}
                hideLabel
                emptyLabel={loadingSocieties ? 'Loading…' : 'Select a society'}
              />
            ) : userSociety ? (
              <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                {`${userSociety.name} - ${userSociety.city}`}
              </div>
            ) : societies.length > 0 ? (
              <SocietySearchableSelect
                societies={societies}
                value={formData.society}
                onChange={handleChange}
                loading={loadingSocieties}
                hideLabel
                emptyLabel={loadingSocieties ? 'Loading…' : 'Select a society'}
              />
            ) : (
              <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                {loadingSocieties ? 'Loading...' : 'No societies available.'}
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter ad title"
            />
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
              Category *
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {categoryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Describe your item or service..."
            />
          </div>

          {/* Price */}
          <div>
            <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
              Price (PKR)
            </label>
            <input
              type="number"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter price in PKR"
            />
          </div>

          {/* Location: user's society + nearest landmark */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            {userSociety && (
              <div className="mb-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                {userSociety.name}{userSociety.city ? `, ${userSociety.city}` : ''}
              </div>
            )}
            <label htmlFor="nearest_landmark" className="block text-sm text-gray-600 mb-1">
              Nearest landmark (optional)
            </label>
            <input
              type="text"
              id="nearest_landmark"
              name="nearest_landmark"
              value={formData.nearest_landmark}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g. near main gate, block B"
            />
          </div>

          {/* Contact Phone */}
          <div>
            <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700 mb-2">
              Contact Phone
            </label>
            <input
              type="tel"
              id="contact_phone"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter contact phone number"
            />
          </div>

          {/* Images */}
          {isEditMode && existingImages.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current images (set cover or remove)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {keptExistingImages.map((img, index) => {
                  const isCover = displayImageIndex === index;
                  return (
                    <div key={img.id} className="relative group">
                      <img
                        src={img.url}
                        alt="Ad"
                        className={`w-full h-24 object-cover rounded-lg border-2 ${
                          isCover ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setDisplayImageIndex(index)}
                        disabled={loading}
                        className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                          isCover
                            ? 'bg-blue-600 text-white'
                            : 'bg-white/90 text-gray-700 hover:bg-white'
                        }`}
                      >
                        {isCover ? 'Cover' : 'Set cover'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setImagesToRemove((prev) => [...prev, img.id]);
                          remapDisplayIndexAfterRemove(index);
                        }}
                        disabled={loading}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <ImageUpload
            selectedImages={selectedImages}
            onImagesChange={setSelectedImages}
            maxImages={Math.max(0, 5 - keptExistingImages.length)}
            disabled={loading}
            autoCompress
            displayImageIndex={displayImageIndex}
            onDisplayImageIndexChange={setDisplayImageIndex}
            imageIndexOffset={keptExistingImages.length}
          />

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                const isSocietyContext = location.pathname.includes('/societies/');
                if (isSocietyContext) {
                  const pathParts = location.pathname.split('/');
                  const societySlugIndex = pathParts.indexOf('societies') + 1;
                  const societySlug = pathParts[societySlugIndex];
                  navigate(`/societies/${societySlug}/ads`);
                } else {
                  navigate('/ads');
                }
              }}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Ad' : 'Create Ad')}
            </button>
          </div>
        </form>

        {/* Info Message */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                Your ad will be reviewed by an admin before being published and visible to the public.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdCreateForm;
